/**
 * @file api-shop-settings.ts
 * @description API-клиент настроек магазина (phase19.13 — почта магазина;
 * phase19.31 — часовой пояс, SC58, cabinet-api-contract.md §8а).
 *
 * Endpoints `/admin/shop/settings` и `/admin/shop/time-zones`. RBAC —
 * SHOP_MANAGE_SETTINGS в API. `apiFetch` уже разворачивает обёртку
 * `{ data, error }`: ошибка приходит `ApiError` с `code`.
 */

import { apiFetch } from "./api-client";

export interface ShopSettingsDto {
  id: string;
  slug: string;
  displayName: string;
  isActive: boolean;
  /** Email магазина для клиентов; null — не задан (письма уйдут с системного). */
  contactEmail: string | null;
  /** Часовой пояс магазина (IANA): «Asia/Yekaterinburg». */
  timeZone: string;
}

/** Группа поясов в списке выбора: сначала Россия, затем остальные. */
export type ShopTimeZoneGroup = "Россия" | "Другие";

/** Пояс в списке выбора (`GET /admin/shop/time-zones`). */
export interface ShopTimeZoneOption {
  /** IANA-имя: «Asia/Yekaterinburg». */
  id: string;
  /** Название по-русски у российских поясов, у части других — латиницей. */
  label: string;
  /** Смещение на сегодня: «+05:00». */
  offset: string;
  offsetMinutes: number;
  group: ShopTimeZoneGroup;
}

/** Код ошибки сервера: неизвестный, пустой или неверный по форме пояс. */
export const SHOP_TIMEZONE_INVALID = "shop_timezone_invalid";

export function getShopSettings(): Promise<ShopSettingsDto> {
  return apiFetch<ShopSettingsDto>("/admin/shop/settings");
}

/**
 * Обновляет настройки магазина. Тело частичное: отсутствующие поля сервер не
 * трогает.
 *
 * Пустая строка в `contactEmail` — команда очистить значение (так же, как
 * `null`); сервер валидирует формат и отвечает 400 «Некорректный email
 * магазина». Неизвестный пояс — 400 `shop_timezone_invalid`.
 */
export function updateShopSettings(payload: {
  contactEmail?: string | null;
  timeZone?: string;
}): Promise<ShopSettingsDto> {
  return apiFetch<ShopSettingsDto>("/admin/shop/settings", {
    method: "PATCH",
    json: payload,
  });
}

/** Список поясов для выбора: «Россия» (27), затем «Другие». */
export function listShopTimeZones(): Promise<ShopTimeZoneOption[]> {
  return apiFetch<ShopTimeZoneOption[]>("/admin/shop/time-zones");
}
